import { HttpErrorResponse } from '@angular/common/http';

/**
 * D4A — the back-office/public envelope every D2/D3A delivery endpoint
 * returns: `{ success, data, error, code }`. Distinct from the
 * TableConnect guest-landing `GuestApiResult` (which is purpose-built
 * per-call). We unwrap it once, here, into a discriminated union so
 * components branch on `ok` and never see a raw `HttpErrorResponse`.
 */
export interface ApiEnvelope<T> {
  success: boolean;
  data?: T | null;
  error?: string | null;
  code?: string | null;
}

export type ApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string; code: string | null };

/** Map a successful envelope to ok/err without leaking transport details. */
export function unwrapEnvelope<T>(res: ApiEnvelope<T>): ApiResult<T> {
  if (res.success && res.data != null) return { ok: true, data: res.data };
  return { ok: false, status: 200, error: safeMessage(res.error), code: res.code ?? null };
}

/** Map an HttpErrorResponse to a safe, user-facing failure result. */
export function classifyHttpError<T>(err: HttpErrorResponse): ApiResult<T> {
  const status = err.status || 0;
  const body = err.error as ApiEnvelope<unknown> | undefined;
  const serverMsg = body && typeof body === 'object' ? body.error : null;
  const code = body && typeof body === 'object' ? (body.code ?? null) : null;
  return { ok: false, status, error: safeMessage(serverMsg, status), code };
}

function safeMessage(serverMsg?: string | null, status?: number): string {
  if (serverMsg && serverMsg.trim().length > 0) return serverMsg.trim();
  switch (status) {
    case 0: return 'Network error. Please check your connection and try again.';
    case 403: return 'Delivery is not available for this store right now.';
    case 404: return 'We couldn’t find that. Please try again.';
    case 409: return 'This order was already received.';
    case 422: return 'This order can’t be placed right now.';
    default: return 'Something went wrong. Please try again.';
  }
}
